import {NodeConfig} from 'konva/lib/Node'
import {DatabasePlantRecord, PlantFields} from '../services/PlantsService'
import {DateString} from './todoList'

export type Plant = {
  id: number
  name: string
  dateOfPlantingUtc?: DateString
  titlePhotoName?: string
  photos?: string[]
  comment?: string
  elementId?: number
  projectId?: string
  gardenPlanVariantId?: string | null
  plantBaseId?: string
  fields?: Partial<PlantFields>
  overwrittenBaseFields?: Partial<PlantFields>
  userId: string
  confidenceInPlantVariety?: boolean
  coordinates?: NodeConfig[]
  collections?: PlantCollection[]
  template?: string
  todos?: {id: number}[]
  isPlantDeleted?: boolean
}

export type PlantCollection = {
  id: number
  name?: string
  color?: string
}

export type PlantCollectionWithPlants = PlantCollection & {plants?: Plant[]}

export type PlantCollectionWithPlantIds = PlantCollection & {
  plantsIds: {plantedPlantsId: number}[]
}

export type PlantWithProjectName = Plant & {projectName?: string; gardenPlanVariantName?: string}

const months = [
  'январь',
  'февраль',
  'март',
  'апрель',
  'май',
  'июнь',
  'июль',
  'август',
  'сентябрь',
  'октябрь',
  'ноябрь',
  'декабрь',
]

export type DatabasePlantRecordFieldGroup =
  | 'main'
  | 'common'
  | 'overview'
  | 'size-appearance'
  | 'growing-conditions'
  | 'properties'
  | 'care'
  | 'plant-health'
  | 'fruiting'
  | 'propagation-planting'

export type DatabasePlantRecordField = {
  group: DatabasePlantRecordFieldGroup
  translationId?: string
  hidden?: boolean
  monthsOptions?: boolean
  tooltip?: boolean
  getValueFromDbRecord?: (
    recordFields: Partial<DatabasePlantRecord['fields']>,
    context: {language: string}
  ) => any
} & (
  | {type: 'text'}
  | {type: 'longtext'}
  | {type: 'enum-dropdown'; options: readonly string[]; multiple: boolean}
  | {type: 'photo'}
  | {type: 'range-start'; rangeId: string}
  | {type: 'range-end'; rangeId: string}
)
export const DatabasePlantRecordMetadataRaw = {
  'Общераспространенное название': {
    type: 'text',
    group: 'main',
    translationId: 'commonName',
  },
  // Сорт_eng: {
  //   type: 'text-composition',
  //   group: 'main',
  //   translationId: 'varietyEng',
  // },
  // Сорт_ru: {
  //   type: 'text-composition',
  //   group: 'main',
  //   translationId: 'varietyRu',
  // },
  Сорт: {
    type: 'text',
    group: 'main',
    translationId: 'variety',
    getValueFromDbRecord(fields, context) {
      if (fields['Сорт_eng'] && !fields['Сорт_ru']) {
        return fields['Сорт_eng']
      } else if (!fields['Сорт_eng'] && fields['Сорт_ru']) {
        return fields['Сорт_ru']
      } else if (fields['Сорт_eng'] && fields['Сорт_ru'] && context.language === 'ru') {
        return `${fields['Сорт_eng']} / ${fields['Сорт_ru']}`
      }

      return fields['Сорт_eng']
    },
  },
  'Уточняющая характеристика': {
    type: 'text',
    group: 'main',
    translationId: 'specification',
  },
  'Фотографии растения': {
    type: 'photo',
    group: 'main',
    translationId: 'plantPhotos',
  },
  'Другие названия': {
    type: 'text',
    group: 'main',
    translationId: 'otherNames',
    getValueFromDbRecord(fields, context) {
      return fields['Другие названия'] && fields['Другие названия'].split('\n').join(', ')
    },
  },
  'Научное (латинское) название': {
    type: 'text',
    group: 'main',
    translationId: 'scienceName',
  },
  'Описание сорта': {
    type: 'text',
    group: 'overview',
    translationId: 'description',
  },

  // Семейство_ru: {
  //   type: 'text-composition',
  //   group: 'main',
  //   translationId: 'familyRu',
  // },
  // Семейство_la: {
  //   type: 'text-composition',
  //   group: 'main',
  //   translationId: 'familyLa',
  // },
  Семейство: {
    type: 'text',
    group: 'main',
    translationId: 'family',
    getValueFromDbRecord(fields, context) {
      if (context.language === 'ru' && fields['Семейство_ru']) {
        return `${fields['Семейство_ru']} / ${fields['Семейство_la']}`
      } else {
        return fields['Семейство_la']
      }
    },
  },

  'Зоны морозостойкости (USDA-зоны)': {
    type: 'enum-dropdown',
    options: ['0', '1', '2', '3', '4', '5', '6', '7', '8', '9', '10', '11', '12'],
    multiple: true,
    group: 'growing-conditions',
    translationId: 'uSDA',
    tooltip: true,
  },
  'Достоинства сорта': {
    type: 'text',
    group: 'overview',
    translationId: 'advantages',
  },
  'Недостатки сорта': {
    type: 'text',
    group: 'overview',
    translationId: 'disadvantages',
  },
  'Сложность ухода': {
    type: 'enum-dropdown',
    options: ['низкая', 'средняя', 'высокая'],
    multiple: false,
    group: 'care',
    translationId: 'careDifficulty',
  },
  'Фотографии листьев': {
    type: 'photo',
    group: 'common',
    translationId: 'leafPhotos',
  },
  'Фотографии цветов': {
    type: 'photo',
    group: 'common',
    translationId: 'flowersPhotos',
  },
  'Фотографии плодов': {
    type: 'photo',
    group: 'common',
    translationId: 'fruitsPhotos',
  },
  'Назначение/местоположение': {
    type: 'enum-dropdown',
    options: ['контейнерное', 'комнатное', 'уличное', 'сезонное'],
    multiple: true,
    group: 'growing-conditions',
    translationId: 'purposeLocation',
  },
  'Жизненный цикл': {
    type: 'enum-dropdown',
    options: ['многолетнее', 'двухлетнее', 'однолетнее'],
    multiple: true,
    group: 'overview',
    translationId: 'lifeCycle',
  },
  Полив: {
    type: 'enum-dropdown',
    options: [
      'частый',
      '1 раз в неделю',
      '2 раза в неделю',
      'умеренный',
      'редкий',
      'по мере необходимости',
      'не требует дополнительного полива',
      'постоянно влажная почва',
    ],
    multiple: false,
    group: 'care',
    translationId: 'watering',
  },
  'Отношение к освещенности': {
    type: 'enum-dropdown',
    options: [
      'солнце',
      'полутень',
      'тень',
      'легкая полутень',
      'рассеянный свет',
      'яркий рассеянный свет',
    ],
    multiple: true,
    group: 'growing-conditions',
    translationId: 'attitudeToLight',
  },

  'Высота от (м)': {
    type: 'range-start',
    rangeId: 'height-rangeField',
    group: 'size-appearance',
    translationId: 'heightFrom',
  },
  'Высота до (м)': {
    type: 'range-end',
    rangeId: 'height-rangeField',
    group: 'size-appearance',
    translationId: 'heightTo',
  },
  'Раскидистость от (м)': {
    type: 'range-start',
    rangeId: 'spreading-rangeField',
    group: 'size-appearance',
    translationId: 'spreadingFrom',
  },
  'Раскидистость до (м)': {
    type: 'range-end',
    rangeId: 'spreading-rangeField',
    group: 'size-appearance',
    translationId: 'spreadingTo',
  },
  'Время цветения': {
    type: 'enum-dropdown',
    options: months,
    multiple: true,
    group: 'size-appearance',
    translationId: 'bloomingTime',
    monthsOptions: true,
  },
  'Цвет цветов': {
    type: 'text',
    group: 'size-appearance',
    translationId: 'flowerColor',
  },

  'Размер цветов от (см)': {
    type: 'range-start',
    rangeId: 'flowerSize-rangeField',
    group: 'size-appearance',
    translationId: 'flowerSize',
  },
  'Размер цветов до (см)': {
    type: 'range-end',
    rangeId: 'flowerSize-rangeField',
    group: 'size-appearance',
    translationId: 'flowerSize',
  },
  'Цвет листьев': {
    type: 'text',
    group: 'size-appearance',
    translationId: 'leafColor',
  },
  'Масса плодов (г)': {
    type: 'text',
    group: 'size-appearance',
    translationId: 'fruitMass',
  },
  Съедобность: {
    type: 'enum-dropdown',
    options: [
      'бутоны',
      'клубни',
      'корни',
      'листья',
      'луковица',
      'медицинские свойства',
      'молодые побеги',
      'овощи',
      'плоды',
      'семена',
      'соцветия',
      'фрукты',
      'ягоды',
    ],
    multiple: true,
    group: 'properties',
    translationId: 'edibility',
  },
  'Медицинские свойства': {
    type: 'text',
    group: 'properties',
    translationId: 'medicalProperties',
  },
  Ядовитость: {
    type: 'enum-dropdown',
    options: [
      'все части',
      'кора',
      'корни',
      'листья',
      'плоды',
      'сок',
      'стебель',
      'цветы',
      'ядовитое',
    ],
    multiple: true,
    group: 'properties',
    translationId: 'toxicity',
  },
  Тип: {
    type: 'enum-dropdown',
    options: [
      'вереск',
      'водное',
      'вьющиеся и лианы',
      'дерево',
      'кактусы и суккуленты',
      'клубнелуковичное',
      'кустарник',
      'луковичное',
      'пальмовые и саговниковые',
      'папоротник',
      'плотоядное',
      'полукустарник',
      'травянистое',
      'цветущее',
      'небольшое дерево',
    ],
    multiple: true,
    group: 'overview',
    translationId: 'type',
  },
  'Факторы привлекательности': {
    type: 'enum-dropdown',
    options: [
      'ампельное',
      'быстрорастущее',
      'всесезонная декоративность',
      'гигрофит',
      'декоративная кора',
      'декоративная форма кроны',
      'декоративное плодоношение',
      'декоративное цветение',
      'декоративнолиственное',
      'ксерофит',
      'медленнорастущее',
      'морозостойкое',
      'очищает воздух',
      'плетущееся',
      'почвопокровное',
      'привлекающее опылителей',
      'продолжительное цветение',
      'съедобное',
      'теневыносливое',
      'тенелюбивое',
      'топиарные формы',
      'хвойное',
      'цветение в ранневесенний период',
      'экзотический внешний вид',
    ],
    multiple: true,
    group: 'common',
    translationId: 'attractionFactors',
  },

  'Особенности выращивания': {
    type: 'enum-dropdown',
    options: [
      'необходима предварительная высадка на рассаду в закрытый грунт',
      'возможна предварительная высадка на рассаду',
      'предварительное проращиевание клубней',
      'стратификация посадочного материала',
      'дополнительная обработка посадочного материала',
      'есть период покоя',
      'возможен посев семян в грунт под зиму',
      'скарификация семян при посадке весной',
    ],
    multiple: true,
    group: 'care',
    translationId: 'featuresOfCultivation',
  },
  'Способы размножения': {
    type: 'enum-dropdown',
    options: [
      'боковыми побегами',
      'делением клубня',
      'делением корневища',
      'делением куста',
      'клубнелуковицами',
      'клубнепочками',
      'листом',
      'луковицами',
      'луковичными чешуйками',
      'отводками',
      'прививкой',
      'семенами',
      'спорами',
      'частью листа',
      'черенкованием',
    ],
    multiple: true,
    group: 'propagation-planting',
    translationId: 'reproductionMethods',
  },
  'Время посадки на рассаду в закрытый грунт': {
    type: 'enum-dropdown',
    options: months,
    multiple: true,
    group: 'propagation-planting',
    translationId: 'timeForPlantingSeedlingsInClosedGround',
    monthsOptions: true,
  },
  'Время посадки на рассаду в открытый грунт': {
    type: 'enum-dropdown',
    options: [...months, 'после минования угрозы заморозков'],
    multiple: true,
    group: 'propagation-planting',
    translationId: 'timeForPlantingSeedlingsInOpenedGround',
    monthsOptions: true,
  },
  'Глубина посадки (см)': {
    type: 'text',
    group: 'propagation-planting',
    translationId: 'plantingDepth',
  },

  'Дополнительные действия по уходу': {
    type: 'enum-dropdown',
    options: [
      'обрезка на зиму',
      'омолаживающая обрезка',
      'поллардинг',
      'прищипывание',
      'санитарная обрезка',
      'удаление увядших цветов',
      'укрытие на зиму',
      'формовочная обрезка',
      'хранение зимой в помещении',
    ], //TODO изменилось поле
    multiple: true,
    group: 'care',
    translationId: 'additionalCareSteps',
  },
  'Время обрезки': {
    type: 'enum-dropdown',
    options: months,
    multiple: true,
    group: 'care',
    translationId: 'trimTime',
    monthsOptions: true,
  },
  'Применение в дизайне': {
    type: 'enum-dropdown',
    options: [
      'аквариум',
      'аллея',
      'альпийская горка (альпинарий)',
      'балкон',
      'берсо',
      'бонсай',
      'бордюр',
      'в интерьере',
      'вертикальное озеленение',
      'гербарий',
      'групповая посадка',
      'декоративный мини-водоем',
      'живая изгородь',
      'кашпо',
      'клумба',
      'контейнер',
      'кулисы',
      'миксбордер',
      'мобильный прудик',
      'оформление букета',
      'пальметта',
      'рабатка',
      'рокарий',
      'солитер (одиночное)',
      'срезка',
      'сухой букет',
      'топиарные фигуры',
      'фитопластика',
      'флористический дизайн',
      'шпалера',
      'японский сад',
    ],
    multiple: true,
    group: 'common',
    translationId: 'applicationInDesign',
  },
  'Скорость роста': {
    type: 'text',
    group: 'overview',
    translationId: 'growthRate',
  },
  'Почва (субстрат)': {
    type: 'text',
    group: 'propagation-planting',
    translationId: 'soil',
  },
  Удобрения: {
    type: 'text',
    group: 'plant-health',
    translationId: 'fertilizers',
  },
  'Частота внесения удобрений': {
    type: 'enum-dropdown',
    options: [
      '1 раз в неделю',
      '2 раза в неделю',
      '1 раз в 2 недели',
      '1 раз в месяц',
      '2 раза в месяц',
      '1 раз в месяц в вегетационный период',
      '2 раза в месяц в вегетационный период',
      '1 раз за сезон',
      '2 раза за сезон',
      '3-4 раза за сезон',
      'при определенных условиях',
    ],
    multiple: true,
    group: 'plant-health',
    translationId: 'fertilizerFrequency',
  },
  Болезни: {
    type: 'enum-dropdown',
    options: [
      'альтернариоз',
      'антракноз',
      'астровый вилт',
      'бактериальный ожог',
      'бактериальная рябуха',
      'белая гниль',
      'белая пятнистость',
      'биаторелловый рак тиса',
      'бурая пятнистость',
      'вертициллез',
      'вершинная гниль',
      'вирус люцерновой мозаики',
      'вирус мозаики',
      'вирус некротической полосатости лилии',
      'вирус хосты X',
      'водянистая гниль',
      'гниль стебля',
      'грибковые заболевания',
      'кила капустных',
      'клястероспориоз',
      'корневая гниль',
      'корневой рак',
      'ложная мучнистая роса',
      'мозаичная болезнь листьев',
      'мокрая бактериальная гниль',
      'монтарь',
      'мучнистая роса',
      'огуречная мозаика',
      'оидиум винограда',
      'парша',
      'пенициллез',
      'песталоциевый некроз',
      'пестролепестность',
      'плодовая гниль (монилиоз)',
      'подгар табака',
      'пожилковый хлороз',
      'пятнистость листьев',
      'рассадная (стеблевая) гниль',
      'реверсия черной смородины',
      'ржавчина',
      'ризоктониоз (бурая гниль)',
      'сажистый гриб',
      'серая гниль',
      'серая плесень',
      'стаганоспороз',
      'стволовая гниль',
      'табачная мозаика',
      'у- вирус картофеля (белая пестрица)',
      'фитофтороз',
      'фомоз',
      'фомоз тиса',
      'фузариоз',
      'фузариозная пятнистость',
      'фузариозное увядание',
      'хлороз',
      'церкоспороз',
      'черная ножка',
      'черная пятнистость',
      'чернь',
      'шютте бурое',
    ],
    multiple: true,
    group: 'plant-health',
    translationId: 'disease',
  },
  Вредители: {
    type: 'enum-dropdown',
    options: [
      'белокрылка',
      'блошка крестоцветная',
      'войлочный ореховый клещ',
      'галлицы',
      'гороховая зерновка',
      'гусеницы',
      'долгоносик',
      'еловая листовертка-иглоед',
      'зонтичная моль',
      'капустная гусеница',
      'капустная муха',
      'клоп вонючий',
      'колорадский жук',
      'комары-долгоножки',
      'корневой луковый клещ',
      'крестоцветная блошка',
      'крот',
      'лилейная муха',
      'лилейный жук',
      'листоблошка',
      'листовая блошка',
      'листовертка',
      'листоеды',
      'луковый клещ',
      'луковая журчалка',
      'луковая муха',
      'малинный жук',
      'медведка',
      'морковная муха',
      'минирующая муха',
      'муравьи',
      'мучнистый червец',
      'мягкая ложнощитовка',
      'нематоды',
      'огневка',
      'паутинный клещ',
      'пилильщик',
      'плодожорка',
      'полевая мышь',
      'полевой клоп',
      'полосатый долгоносик',
      'почковый клещ',
      'проволочник',
      'растительноядный клещ',
      'самшитовая огневка',
      'сельдерейная муха',
      'слизни',
      'слюнявая пенница',
      'стеблевая нематода',
      'табачный проволочник',
      'тисовая галлица',
      'тисовая ложнощитовка',
      'тисовая плоскотелка',
      'тисовый почковый клещ',
      'тля',
      'трипсы',
      'тюльпановая огнёвка',
      'улитки',
      'фасолевая зерновка',
      'хрущ',
      'цветочная пяденица',
      'щитовка',
    ],
    multiple: true,
    group: 'plant-health',
    translationId: 'pests',
  },
  Пестициды: {
    type: 'text',
    group: 'plant-health',
    translationId: 'pesticides',
  },
  'Частота обработки пестицидами': {
    type: 'enum-dropdown',
    options: [
      '1 раз в неделю',
      '2 раза в неделю',
      '1 раз в месяц',
      '2 раза в месяц',
      '1 раз за сезон',
      'по мере необходимости',
      'при определенных условиях',
    ],
    multiple: false,
    group: 'plant-health',
    translationId: 'pesticideTreatmentFrequency',
  },

  'Время плодоношения': {
    type: 'enum-dropdown',
    options: months,
    multiple: true,
    group: 'fruiting',
    translationId: 'fruitingTime',
    monthsOptions: true,
  },
  'Особенности хранения плодов': {
    type: 'text',
    group: 'fruiting',
    translationId: 'featuresOfFruitStorage',
  },
  'Особенности хранения посадочного материала': {
    type: 'text',
    group: 'propagation-planting',
    translationId: 'featuresOfStoringPlantingMaterial',
  },
} as const satisfies Record<keyof PlantFields, DatabasePlantRecordField>

export const DatabasePlantRecordMetadata: Record<keyof PlantFields, DatabasePlantRecordField> =
  DatabasePlantRecordMetadataRaw
